import { useState } from "react";
import "./styles.css";
import jsPDF from "jspdf";
import React from "react";
import logo from "./assets/Trago-Calc-Logo.jpg"; // Local logo import

// --- Extend window to include dataLayer ---
declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

type Carpet = {
  id: string;
  title: string;
  desc: React.ReactNode;
  price: number;
};

export default function CarpetCalculator() {
  const [length, setLength] = useState<number>(0);
  const [width, setWidth] = useState<number>(0);
  const [carpet, setCarpet] = useState<Carpet | null>(null);
  const [interactionTracked, setInteractionTracked] = useState(false);

  const carpets: Carpet[] = [
    { id: "clearance", title: "General Domestic", desc: <>Great quality at unbeatable prices.</>, price: 4.17 },
    { id: "standard", title: "Heavy Domestic", desc: <>Good balance of comfort and durability.</>, price: 8.36 },
    { id: "luxury", title: "Luxury Pile", desc: <>Top quality, luxurious feel.</>, price: 10.75 },
  ];

  const area = Math.max(0, length) * Math.max(0, width);
  const total = carpet ? (area * carpet.price).toFixed(2) : "0.00";
  const LOGO_URL = logo; // Use local image here

  // --- Track interaction only once ---
  const trackInteractionOnce = () => {
    if (!interactionTracked) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "calc_Interaction" });
      setInteractionTracked(true);
    }
  };

  const handleDimensionChange = (setter: React.Dispatch<React.SetStateAction<number>>, value: string) => {
    setter(value === "" ? 0 : parseFloat(value));
    trackInteractionOnce();
  };

  const handleCarpetChange = (c: Carpet) => {
    setCarpet(c);
    trackInteractionOnce();
  };

  // --- PDF download + GTM tracking ---
  const downloadPDF = () => {
    if (!carpet) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "calc_pdfDL", calc_price: parseFloat(total) });

    const doc = new jsPDF();
    let y = 40;
    const labelX = 20;
    const valueX = 65;
    const rowGap = 7;

    doc.addImage(LOGO_URL, "JPEG", 15, 10, 60, 27); // Local logo
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Trago Carpet Price Guide", 20, y);
    y += 10;

    doc.setDrawColor(210);
    doc.line(20, y, 190, y);
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Room Size", 20, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("Room Length (m):", labelX, y);
    doc.setFont("helvetica", "bold");
    doc.text(`${length}m`, valueX, y);
    y += rowGap;

    doc.setFont("helvetica", "normal");
    doc.text("Room Width (m):", labelX, y);
    doc.setFont("helvetica", "bold");
    doc.text(`${width}m`, valueX, y);
    y += rowGap;

    doc.setFont("helvetica", "normal");
    doc.text("Room Size:", labelX, y);
    doc.setFont("helvetica", "bold");
    doc.text(`${area.toFixed(2)} m²`, valueX, y);
    y += 10;

    doc.setDrawColor(210);
    doc.line(20, y, 190, y);
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Carpet Options", 20, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("Selected Carpet:", labelX, y);

    const rangeWidth = doc.getTextWidth(carpet.title);
    doc.setFont("helvetica", "bold");
    doc.text(carpet.title, valueX, y);

    doc.setFont("helvetica", "normal");
    doc.text(
      ` - ${typeof carpet.desc === "string" ? carpet.desc : (carpet.desc as any).props?.children || ""}`,
      valueX + rangeWidth + 2,
      y
    );
    y += 10;

    doc.setDrawColor(210);
    doc.line(20, y, 190, y);
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Total", 20, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("Trago Price from:", labelX, y);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(`£${total}`, valueX, y);
    y += 12;

    // --- New line of body copy ---
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(
      "Take this estimate into your local store and speak to one of our Carpet Specialists, and jump the queue and book an appointment on our website.",
      20,
      y,
      { maxWidth: 170 }
    );

    const isSafari = navigator.userAgent.includes("Safari") && !navigator.userAgent.includes("Chrome");
    const isFirefox = navigator.userAgent.includes("Firefox");

    if (isSafari || isFirefox) {
      const blobUrl = doc.output("bloburl");
      window.open(blobUrl, "_blank");
    } else {
      doc.save("My-Trago-Carpet-Quote.pdf");
    }
  };

  return (
    <div className="calculator">
      <h2>Trago Carpet Calculator</h2>
      <p className="calculator-subtitle">Get a quick guide to how cheap it is to carpet a room at Trago.</p>

      <div className="dimension-row">
        <label>
          <span className="dimension-label">Length (m)</span>
          <input
            type="number"
            min="0"
            step="1"
            placeholder="0"
            value={length === 0 ? "" : length}
            onChange={(e) => handleDimensionChange(setLength, e.target.value)}
          />
        </label>

        <span className="multiply">×</span>

        <label>
          <span className="dimension-label">Width (m)</span>
          <input
            type="number"
            min="0"
            step="1"
            placeholder="0"
            value={width === 0 ? "" : width}
            onChange={(e) => handleDimensionChange(setWidth, e.target.value)}
          />
        </label>

        <span className="equals">= {area.toFixed(2)} m²</span>
      </div>

      <div className="carpets">
        <p>Choose Carpet Type:</p>
        {carpets.map((c) => (
          <label key={c.id} className="carpet-option">
            <input
              type="radio"
              name="carpet"
              value={c.id}
              checked={carpet?.id === c.id}
              onChange={() => handleCarpetChange(c)}
            />
            <span className="title">{c.title}</span> – {c.desc}
          </label>
        ))}
      </div>

      <div className="total">
        <p>Trago Price from:</p>
        <p className="value">£{total}</p>
      </div>

      <p className="book-appointment">
        <strong>Now book an appointment below, with one of our carpet specialists.</strong>
      </p>

      <div className="button-row">
        <button className="download-pdf" onClick={downloadPDF}>
          Download PDF
        </button>

        <a
          className="book-appointment-btn"
          href="https://www.trago.co.uk/best-price-carpets/#appointment"
          target="_blank"
          rel="noopener noreferrer"
        >
          Book an Appointment
        </a>
      </div>
    </div>
  );
}
